import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import "../App.css";
import {mobile} from "../services/mobileBridge";

const IMAGES = [
  "/jigsaw/jigsaw_01_sunset.jpg",
  "/jigsaw/jigsaw_02_market.jpg",
  "/jigsaw/jigsaw_03_elephant.jpg",
  "/jigsaw/jigsaw_04_beach.jpg",
  "/jigsaw/jigsaw_05_city.jpg",
  "/jigsaw/jigsaw_06_garden.jpg",
  "/jigsaw/jigsaw_07_mountains.jpg",
  "/jigsaw/jigsaw_08_waterfall.jpg",
  "/jigsaw/jigsaw_09_cats.jpg",
  "/jigsaw/jigsaw_10_aurora.jpg",
];

const DIFFICULTIES = [
  { label: "Easy", grid: 3 },
  { label: "Medium", grid: 4 },
  { label: "Hard", grid: 5 },
  { label: "Expert", grid: 6 },
] as const;

const TAB = 0.24; // tab size as fraction of piece side length
const SNAP = 0.34; // snap threshold as fraction of piece side length

type Edge = 0 | 1 | -1; // flat | tab (protrudes out) | blank (indents in)

interface PieceMeta {
  top: Edge;
  right: Edge;
  bottom: Edge;
  left: Edge;
}

interface Piece extends PieceMeta {
  id: number;
  row: number;
  col: number;
  cx: number; // center x in container px
  cy: number; // center y in container px
  rot: number; // rotation in deg
  placed: boolean;
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

// Build the SVG path for one jigsaw piece given its 4 edges.
// Coordinates are in the piece's local bounding box (with TAB margin on all sides).
function buildPiecePath(edges: PieceMeta, p: number, E: number): string {
  const { top, right, bottom, left } = edges;
  // Base rectangle corners (offset by E margin so tabs fit inside bbox).
  const TL = [E, E];
  const TR = [E + p, E];
  const BR = [E + p, E + p];
  const BL = [E, E + p];

  // Edge drawing: side(x0,y0,x1,y1,t,nx,ny)
  // t: 0 flat, 1 tab out, -1 blank in. (nx,ny): outward unit normal.
  const side = (x0: number, y0: number, x1: number, y1: number, t: Edge, nx: number, ny: number) => {
    if (t === 0) return `L ${x1.toFixed(2)} ${y1.toFixed(2)}`;
    const L = Math.hypot(x1 - x0, y1 - y0);
    const ux = (x1 - x0) / L;
    const uy = (y1 - y0) / L;
    const s = t; // +1 tab, -1 blank
    const H = TAB * L; // tab height
    const a = 0.4, b = 0.6; // neck start/end fractions
    const ax = x0 + ux * a * L, ay = y0 + uy * a * L;
    const bx = x0 + ux * b * L, by = y0 + uy * b * L;
    const mx = x0 + ux * 0.5 * L, my = y0 + uy * 0.5 * L;
    const apx = mx + nx * s * H, apy = my + ny * s * H;
    // Two symmetric cubic beziers forming a rounded bulb.
    const c1x = ax + ux * 0.05 * L, c1y = ay + uy * 0.05 * L;
    const c2x = apx - ux * 0.14 * L, c2y = apy - uy * 0.14 * L;
    const c3x = apx + ux * 0.14 * L, c3y = apy + uy * 0.14 * L;
    const c4x = bx - ux * 0.05 * L, c4y = by - uy * 0.05 * L;
    return (
      `L ${ax.toFixed(2)} ${ay.toFixed(2)} ` +
      `C ${c1x.toFixed(2)} ${c1y.toFixed(2)} ${c2x.toFixed(2)} ${c2y.toFixed(2)} ${apx.toFixed(2)} ${apy.toFixed(2)} ` +
      `C ${c3x.toFixed(2)} ${c3y.toFixed(2)} ${c4x.toFixed(2)} ${c4y.toFixed(2)} ${bx.toFixed(2)} ${by.toFixed(2)} ` +
      `L ${x1.toFixed(2)} ${y1.toFixed(2)}`
    );
  };

  let d = `M ${TL[0]} ${TL[1]} `;
  d += side(TL[0], TL[1], TR[0], TR[1], top, 0, -1) + " ";
  d += side(TR[0], TR[1], BR[0], BR[1], right, 1, 0) + " ";
  d += side(BR[0], BR[1], BL[0], BL[1], bottom, 0, 1) + " ";
  d += side(BL[0], BL[1], TL[0], TL[1], left, -1, 0) + " ";
  d += "Z";
  return d;
}

// Generate complementary edges for every piece in an NxN grid.
function generateEdges(N: number): PieceMeta[] {
  const rand = () => (Math.random() < 0.5 ? 1 : -1) as Edge;
  const metas: PieceMeta[] = [];
  for (let r = 0; r < N; r++) {
    for (let c = 0; c < N; c++) {
      const top: Edge = r === 0 ? 0 : (-metas[(r - 1) * N + c].bottom as Edge);
      const left: Edge = c === 0 ? 0 : (-metas[r * N + (c - 1)].right as Edge);
      const bottom: Edge = r === N - 1 ? 0 : rand();
      const right: Edge = c === N - 1 ? 0 : rand();
      metas.push({ top, right, bottom, left });
    }
  }
  return metas;
}

export default function JigsawPuzzle({ country }: { country: "south-africa" | "nigeria" }) {
  const [imageIndex, setImageIndex] = useState<number>(() => Math.floor(Math.random() * IMAGES.length));
  const [gridSize, setGridSize] = useState(3);
  const [pieces, setPieces] = useState<Piece[]>([]);
  const [moves, setMoves] = useState(0);
  const [seconds, setSeconds] = useState(0);
  const [solved, setSolved] = useState(false);
  const [started, setStarted] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [boardPx, setBoardPx] = useState(440);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const dragRef = useRef<{ id: number; offX: number; offY: number } | null>(null);
  const boardPxRef = useRef(boardPx);
  boardPxRef.current = boardPx;

  const N = gridSize;
  const p = boardPx / N; // piece base side length
  const E = TAB * p; // tab extent / bbox margin
  const trayPx = boardPx * 0.95;
  const containerH = boardPx + trayPx;
  const currentImage = IMAGES[imageIndex];

  // Measure container to pick a responsive board size.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => {
      const w = el.clientWidth;
      const next = Math.max(280, Math.min(Math.floor(w - 12), 540));
      setBoardPx((prev) => (Math.abs(prev - next) > 4 ? next : prev));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const prevBoardPxRef = useRef(boardPx);

  // When board size changes, rescale piece positions proportionally and re-snap placed ones.
  useEffect(() => {
    const prev = prevBoardPxRef.current;
    prevBoardPxRef.current = boardPx;
    if (prev === boardPx || pieces.length === 0) return;
    const ratio = boardPx / prev;
    setPieces((prevPieces) =>
      prevPieces.map((pc) => {
        const newP = boardPx / N;
        const slotCx = pc.col * newP + newP / 2;
        const slotCy = pc.row * newP + newP / 2;
        if (pc.placed) {
          return { ...pc, cx: slotCx, cy: slotCy };
        }
        return { ...pc, cx: pc.cx * ratio, cy: pc.cy * ratio };
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardPx]);

  const startTimer = useCallback(() => {
    if (timerRef.current) return;
    timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    setStarted(true);
  }, []);

  // Initialise a new game / shuffle.
  const initGame = useCallback(() => {
    const metas = generateEdges(N);
    const newPieces: Piece[] = metas.map((m, i) => {
      const row = Math.floor(i / N);
      const col = i % N;
      const half = p / 2;
      // Scatter in the tray (below the board).
      const cx = Math.random() * (boardPx - p) + half;
      const cy = boardPx + Math.random() * (trayPx - p) + half;
      const rot = (Math.random() - 0.5) * 16; // slight tilt
      return { id: i, row, col, cx, cy, rot, placed: false, ...m };
    });
    setPieces(newPieces);
    setMoves(0);
    setSeconds(0);
    setSolved(false);
    setStarted(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, [N, p, boardPx, trayPx]);

  useEffect(() => {
    initGame();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageIndex, gridSize]);

  // Global pointer move/up while dragging.
  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      e.preventDefault();
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const px = e.clientX - rect.left;
      const py = e.clientY - rect.top;
      const ncx = px - drag.offX;
      const ncy = py - drag.offY;
      const half = (p + 2 * E) / 2;
      const maxX = boardPx - half;
      const maxY = containerH - half;
      const clampedCx = Math.max(half, Math.min(maxX, ncx));
      const clampedCy = Math.max(half, Math.min(maxY, ncy));
      setPieces((prev) =>
        prev.map((pc) => (pc.id === drag.id ? { ...pc, cx: clampedCx, cy: clampedCy, rot: 0 } : pc))
      );
    };
    const onUp = (e: PointerEvent) => {
      const drag = dragRef.current;
      dragRef.current = null;
      if (!drag) return;
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      // Snap check
      setPieces((prev) => {
        let placedNow = false;
        const next = prev.map((pc) => {
          if (pc.id !== drag.id) return pc;
          const slotCx = pc.col * p + p / 2;
          const slotCy = pc.row * p + p / 2;
          const dist = Math.hypot(pc.cx - slotCx, pc.cy - slotCy);
          if (dist < SNAP * p) {
            placedNow = true;
            return { ...pc, cx: slotCx, cy: slotCy, rot: 0, placed: true };
          }
          return pc;
        });
        if (placedNow) {
          setMoves((m) => m + 1);
          const allPlaced = next.every((pc) => pc.placed);
          if (allPlaced) {
            if (timerRef.current) {
              clearInterval(timerRef.current);
              timerRef.current = null;
            }
            mobile.haptic("success");
            setTimeout(() => setSolved(true), 350);
          }
        }
        return next;
      });
    };
    window.addEventListener("pointermove", onMove, { passive: false });
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [p, E, boardPx, trayPx, containerH]);

  const onPiecePointerDown = useCallback(
    (e: React.PointerEvent, piece: Piece) => {
      if (piece.placed || solved) return;
      e.preventDefault();
      if (!started) startTimer();
      (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const px = e.clientX - rect.left;
      const py = e.clientY - rect.top;
      dragRef.current = { id: piece.id, offX: px - piece.cx, offY: py - piece.cy };
      // Lift the dragged piece to the top of the render order.
      setPieces((prev) => {
        const idx = prev.findIndex((pc) => pc.id === piece.id);
        if (idx === -1) return prev;
        const copy = [...prev];
        const [item] = copy.splice(idx, 1);
        copy.push(item);
        return copy;
      });
    },
    [solved, started, startTimer]
  );

  const newImage = useCallback(() => {
    if (IMAGES.length <= 1) {
      setImageIndex(0);
      return;
    }
    let idx = imageIndex;
    for (let attempt = 0; attempt < 20; attempt++) {
      idx = Math.floor(Math.random() * IMAGES.length);
      if (idx !== imageIndex) {
        setImageIndex(idx);
        return;
      }
    }
    setImageIndex((imageIndex + 1) % IMAGES.length);
  }, [imageIndex]);

  // Hint: auto-place one random unplaced piece.
  const hint = useCallback(() => {
    setPieces((prev) => {
      const unplaced = prev.filter((pc) => !pc.placed);
      if (unplaced.length === 0) return prev;
      const pick = unplaced[Math.floor(Math.random() * unplaced.length)];
      if (!started) startTimer();
      setMoves((m) => m + 1);
      const next = prev.map((pc) =>
        pc.id === pick.id
          ? { ...pc, cx: pc.col * p + p / 2, cy: pc.row * p + p / 2, rot: 0, placed: true }
          : pc
      );
      const allPlaced = next.every((pc) => pc.placed);
      if (allPlaced) {
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        mobile.haptic("success");
        setTimeout(() => setSolved(true), 350);
      }
      return next;
    });
  }, [p, started, startTimer]);

  const placedCount = useMemo(() => pieces.filter((pc) => pc.placed).length, [pieces]);

  return (
    <div className="container jigsaw-page">
      <a href={`/game-studio/${country}`} className="game-back-link">
        &larr; Back to Games
      </a>

      <div className="jigsaw-controls">
        <div className="jigsaw-diff-group">
          {DIFFICULTIES.map((d) => (
            <button
              key={d.grid}
              className={`jigsaw-diff-btn ${gridSize === d.grid ? "active" : ""}`}
              onClick={() => setGridSize(d.grid)}
            >
              {d.label}
            </button>
          ))}
        </div>

        <div className="jigsaw-stats">
          <div className="jigsaw-stat">
            <span className="jigsaw-stat-label">Placed</span>
            <span className="jigsaw-stat-val">{placedCount}/{pieces.length}</span>
          </div>
          <div className="jigsaw-stat">
            <span className="jigsaw-stat-label">Moves</span>
            <span className="jigsaw-stat-val">{moves}</span>
          </div>
          <div className="jigsaw-stat">
            <span className="jigsaw-stat-label">Time</span>
            <span className="jigsaw-stat-val">{formatTime(seconds)}</span>
          </div>
        </div>

        <div className="jigsaw-progress">
          <div
            className="jigsaw-progress-bar"
            style={{ width: `${pieces.length ? (placedCount / pieces.length) * 100 : 0}%` }}
          />
        </div>

        <div className="jigsaw-actions">
          <button className="jigsaw-action-btn" onClick={() => setShowPreview((v) => !v)}>
            {showPreview ? "Hide" : "Preview"}
          </button>
          <button className="jigsaw-action-btn" onClick={hint}>
            Hint
          </button>
          <button className="jigsaw-action-btn" onClick={newImage}>
            New Image
          </button>
          <button className="jigsaw-action-btn" onClick={initGame}>
            Reshuffle
          </button>
        </div>
      </div>

      {showPreview && (
        <div className="jigsaw-preview-wrapper" onClick={() => setShowPreview(false)}>
          <img src={currentImage} alt="Puzzle preview" className="jigsaw-preview" />
        </div>
      )}

      <div
        ref={containerRef}
        className="jigsaw-stage"
        style={{ width: boardPx, height: containerH }}
      >
        {/* Board */}
        <div
          className="jigsaw-board"
          style={{ width: boardPx, height: boardPx, background: `url(${currentImage})`, backgroundSize: "cover", backgroundPosition: "center" }}
        >
          <svg className="jigsaw-board-ghost" viewBox={`0 0 ${boardPx} ${boardPx}`} width={boardPx} height={boardPx}>
            {Array.from({ length: N }).map((_, r) =>
              Array.from({ length: N }).map((_, c) => (
                <rect
                  key={`${r}-${c}`}
                  x={c * p + 1}
                  y={r * p + 1}
                  width={p - 2}
                  height={p - 2}
                  rx={4}
                  className="jigsaw-slot"
                />
              ))
            )}
          </svg>
        </div>

        {/* Tray label */}
        <div className="jigsaw-tray-label" style={{ top: boardPx + 6 }}>Tray — drag pieces to the board</div>

        {/* Pieces */}
        {pieces.map((pc) => {
          const half = (p + 2 * E) / 2;
          const d = buildPiecePath(pc, p, E);
          const clipId = `clip-${pc.id}`;
          return (
            <div
              key={pc.id}
              className={`jigsaw-piece-drag ${pc.placed ? "placed" : ""}`}
              style={{
                width: p + 2 * E,
                height: p + 2 * E,
                transform: `translate(${pc.cx - half}px, ${pc.cy - half}px) rotate(${pc.rot}deg)`,
              }}
              onPointerDown={(e) => onPiecePointerDown(e, pc)}
            >
              <svg
                viewBox={`0 0 ${p + 2 * E} ${p + 2 * E}`}
                width={p + 2 * E}
                height={p + 2 * E}
                className="jigsaw-piece-svg"
              >
                <defs>
                  <clipPath id={clipId}>
                    <path d={d} />
                  </clipPath>
                  <filter id={`sh-${pc.id}`} x="-20%" y="-20%" width="140%" height="140%">
                    <feDropShadow dx="0" dy="2" stdDeviation="2" floodColor="#000" floodOpacity="0.45" />
                  </filter>
                </defs>
                <g clipPath={`url(#${clipId})`}>
                  <image
                    href={currentImage}
                    x={E - pc.col * p}
                    y={E - pc.row * p}
                    width={boardPx}
                    height={boardPx}
                    preserveAspectRatio="xMidYMid slice"
                  />
                </g>
                <path d={d} fill="none" className="jigsaw-piece-edge" />
              </svg>
            </div>
          );
        })}
      </div>

      {solved && (
        <div className="jigsaw-victory-overlay" onClick={newImage}>
          <div className="jigsaw-victory-card" onClick={(e) => e.stopPropagation()}>
            <div className="jigsaw-confetti">
              {Array.from({ length: 18 }).map((_, i) => (
                <span key={i} style={{ "--i": i } as React.CSSProperties} />
              ))}
            </div>
            <div className="jigsaw-victory-emoji">🧩</div>
            <h2>Puzzle Complete!</h2>
            <p>
              {gridSize}×{gridSize} &middot; {moves} moves &middot; {formatTime(seconds)}
            </p>
            <button className="center-btn" onClick={newImage}>
              Next Puzzle →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
